import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  SEARCH_EXAMPLES,
  SEARCH_KINDS,
  SEARCH_LIMITS,
  SEARCH_MSG,
  searchAnswerMessage,
  searchGroupLabelOf,
  searchKindLabelOf,
  searchStatusLabel,
  yenText,
  type SearchAnswer,
  type SearchHit,
  type SearchKind,
  type SearchResult,
  type UnitPriceHistory,
} from "@genbaryoku/contract";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api, ApiError } from "../api.js";
import { useMe } from "../auth.js";
import { useUses } from "../core/uses.js";
import { t } from "../labels.js";
import { useNarrow } from "../lib/useNarrow.js";
import { Help } from "./ui.js";

import { ymdShort } from "../lib/dates.js";
/**
 * 横断検索の窓（#224、docs/90 §7・§11.2 段5）。
 *   PC：上の帯の中央の欄（浮く名前「検索」・例の文は置かない・⌘K／Ctrl+K で欄へ）。押すと下に幅 720px の窓
 *     入力中＝打つたびに 150ms 待って1回 GET /search（種類ごとに 5 件と「すべて n件 ›」）。前の結果を残して入れ替える（#179）
 *     Enter（か窓の「AI回答」）＝POST /search/answer。窓のいちばん上に「AI回答」の枠＝押した瞬間に決まった高さを取り、答えが届いても下の結果をずらさない
 *   スマホ（幅 767px 以下）：上の帯の ☰ の右に虫眼鏡（docs/90 決定 6）→ 全面のシートで同じ中身・押す所 56px。音声は OS のキーボードの音声入力（決定 7）
 * 種類は会社の「使う機能」で絞る（{vendorQuote}・見積依頼・受け取った書類）。行の操作は 詳細・PDF（帳票）・単価履歴（明細）（docs/90 §11.1 ス）
 * #224 の手直し（docs/90 §13、2026-10-21 射場さん）：
 *   - AI回答は会社ごと（Me.features.aiAnswer＝運営が有効にした会社だけ）。無い会社は「Enter で AI回答」・ボタン・質問例を出さず、Enter は入力中の検索をすぐ引くだけ
 *   - 入力中の検索は 打ち終わりから 300ms・2文字以上（SEARCH_LIMITS.debounceMs・minChars）
 *   - PC の窓は画面の幅いっぱい（左右 16px・1400px まで）。欄が空なら「質問例」5つ（押すとその質問で AI回答）
 *   - AI回答の頭に解釈の1行（API が組み立てた文＝SearchAnswer.interpretation）
 */
export function SearchBox({ onHelp }: { onHelp: (from: HTMLElement | null) => void }) {
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const panel = useSearchPanel(() => close());
  const ai = panel.ai;

  function close() {
    setOpen(false);
    if (narrow) requestAnimationFrame(() => opener.current?.focus());
  }

  // ⌘K／Ctrl+K で欄へ（スマホはシートを開く）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => input.current?.focus());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // 窓の外を押したら閉じる（PC）
  useEffect(() => {
    if (!open || narrow) return;
    const onDown = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, narrow]);

  const field = (
    <div className={"tsearch-field" + (panel.q ? " is-filled" : "")}>
      <svg className="tsearch-icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="M15.5 15.5L21 21" />
      </svg>
      <input
        ref={input}
        id={panel.inputId}
        type="search"
        autoComplete="off"
        enterKeyHint="search"
        placeholder=" "
        maxLength={SEARCH_LIMITS.answerQMax}
        value={panel.q}
        onChange={(e) => {
          panel.setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            if (narrow) close();
            else {
              setOpen(false);
              input.current?.blur();
            }
          } else if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            setOpen(true);
            panel.ask();
          } else if (e.key === "ArrowDown") {
            const first = box.current?.querySelector<HTMLElement>("[data-srow]");
            if (first) {
              e.preventDefault();
              first.focus();
            }
          }
        }}
        aria-controls={panel.listId}
        data-testid="search-input"
      />
      <label htmlFor={panel.inputId}>{SEARCH_MSG.label}</label>
    </div>
  );

  if (narrow) {
    return (
      <>
        <button type="button" ref={opener} className="tsearch-open" aria-label={SEARCH_MSG.open} onClick={() => { setOpen(true); requestAnimationFrame(() => input.current?.focus()); }} data-testid="search-open">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="M15.5 15.5L21 21" />
          </svg>
        </button>
        {open ? (
          <div className="tsearch-sheet" role="dialog" aria-modal="true" aria-label={SEARCH_MSG.label} ref={box} data-testid="search-sheet" data-notice-above>
            <div className="tsearch-sheethead">
              {field}
              <button type="button" className="btn text" onClick={close} data-testid="search-close">
                {SEARCH_MSG.close}
              </button>
            </div>
            <div className="tsearch-body">{panel.body({ onHelp: (el) => { close(); onHelp(el); } })}</div>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <div className="tsearch" ref={box} data-testid="search-box">
      {field}
      {open && (panel.q.trim() || panel.answer || ai) ? (
        <div className="tsearch-pop" data-testid="search-pop">
          {panel.body({ onHelp: (el) => { setOpen(false); onHelp(el); } })}
        </div>
      ) : null}
    </div>
  );
}

type AnswerState = { q: string; loading: boolean; data: SearchAnswer | null; message: string };

/** 窓の中身（PC の窓とスマホのシートで同じ） */
function useSearchPanel(onNavigate: () => void) {
  const use = useUses();
  const me = useMe();
  const ai = me.data?.features.aiAnswer === true;
  const navigate = useNavigate();
  const inputId = useId();
  const listId = useId();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState<SearchKind | null>(null);
  const [answer, setAnswer] = useState<AnswerState | null>(null);
  const [prices, setPrices] = useState<string | null>(null);
  const kinds = SEARCH_KINDS.filter(
    (k) => (k !== "vendor_quote" || use("vendorQuote")) && (k !== "rfq" || use("rfq")) && (k !== "received_document" || use("receivedDocs")) && (k !== "order" || !!me.data?.tenant.modules.includes("sales")),
  );
  const kindsParam = kinds.join(",");

  // 入力中の検索：打ち終わりから 300ms・2文字以上（docs/90 §13.5）
  const typed = (v: string) => {
    const t = v.trim().slice(0, SEARCH_LIMITS.qMax);
    return t.length >= SEARCH_LIMITS.minChars ? t : "";
  };
  useEffect(() => {
    const id = setTimeout(() => setDebounced(typed(q)), SEARCH_LIMITS.debounceMs);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    setExpanded(null);
    setPrices(null);
  }, [debounced]);

  const result = useQuery({
    queryKey: ["search", debounced, kindsParam],
    queryFn: () => api<SearchResult>("GET", `/search?q=${encodeURIComponent(debounced)}&kinds=${kindsParam}`),
    enabled: debounced.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const more = useQuery({
    queryKey: ["search", debounced, expanded, "all"],
    queryFn: () => api<SearchResult>("GET", `/search?q=${encodeURIComponent(debounced)}&kinds=${expanded}&limit=${SEARCH_LIMITS.expanded}`),
    enabled: debounced.length > 0 && expanded !== null,
    staleTime: 10_000,
  });
  const priceQ = useQuery({
    queryKey: ["search-prices", prices],
    queryFn: () => api<UnitPriceHistory>("GET", `/search/unit-prices?name=${encodeURIComponent(prices ?? "")}`),
    enabled: prices !== null,
  });

  const ask = (asked?: string) => {
    const text = (asked ?? q).trim();
    if (!text) return;
    if (asked != null) setQ(asked);
    // AI回答の無い会社：Enter は入力中の検索を待たずに引くだけ（AI を呼ばない）
    if (!ai) {
      setDebounced(typed(text));
      return;
    }
    setAnswer({ q: text, loading: true, data: null, message: SEARCH_MSG.aiMaking });
    api<SearchAnswer>("POST", "/search/answer", { q: text, kinds })
      .then((a) => setAnswer((cur) => (cur && cur.q === text ? { q: text, loading: false, data: a, message: a.status === "ok" ? "" : searchAnswerMessage(a.status) } : cur)))
      .catch((e: unknown) => {
        const limit = e instanceof ApiError && e.code === "SEARCH_ANSWER_LIMIT" ? (e.problem.limit === "day" ? SEARCH_MSG.aiLimitDay : SEARCH_MSG.aiLimitMinute) : null;
        setAnswer((cur) => (cur && cur.q === text ? { q: text, loading: false, data: null, message: limit ?? SEARCH_MSG.aiFailed } : cur));
      });
  };

  const go = (url: string) => {
    onNavigate();
    navigate(url);
  };

  const groups = result.data && debounced ? result.data.groups : [];
  const groupOf = (k: SearchKind) => (expanded === k && more.data ? (more.data.groups.find((g) => g.kind === k) ?? null) : null);

  const body = ({ onHelp }: { onHelp: (el: HTMLElement | null) => void }) => (
    <>
      {ai ? (
        <div className="tsearch-head">
          <span className="tsearch-hint">{SEARCH_MSG.enterHint}</span>
          <Help text={SEARCH_MSG.aiHelp} label={`${SEARCH_MSG.aiTitle}の説明`} testId="search-help" />
          <span className="sp" />
          <button type="button" className="btn tonal" onClick={() => ask()} disabled={!q.trim() || answer?.loading} data-testid="search-ask">
            {SEARCH_MSG.aiTitle}
          </button>
        </div>
      ) : null}
      {ai && !q.trim() && !answer ? (
        <section className="tsearch-examples" data-testid="search-examples">
          <h3>{SEARCH_MSG.examples}</h3>
          <ul>
            {SEARCH_EXAMPLES.map((x) => (
              <li key={x}>
                <button type="button" className="btn text tsearch-example" onClick={() => ask(t(x))} data-testid="search-example">
                  {t(x)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {answer ? (
        <section className={"tsearch-ans" + (answer.loading ? " is-busy" : "")} aria-live="polite" aria-busy={answer.loading} data-testid="search-answer">
          <h3>{SEARCH_MSG.aiTitle}</h3>
          {answer.data && answer.data.interpretation.length > 0 ? (
            <p className="tsearch-interp" data-testid="search-interpretation">
              <span className="tsearch-interplabel">{SEARCH_MSG.interpretation}</span>
              <span>{answer.data.interpretation.map((x) => t(x)).join("／")}</span>
            </p>
          ) : null}
          {answer.loading || answer.message ? <p className="tsearch-ansmsg">{answer.message}</p> : null}
          {answer.data?.sentences.map((s, i) => (
            <p key={i} className="tsearch-anstext">
              {s}
            </p>
          ))}
          {answer.data && answer.data.evidence.length > 0 ? (
            <div className="tsearch-evid">
              <span className="tsearch-evidlabel">{SEARCH_MSG.evidence}</span>
              {answer.data.evidence.map((e) => (
                <a
                  key={`${e.kind}:${e.id}`}
                  href={e.url}
                  className="chip tsearch-evlink"
                  onClick={(ev) => {
                    ev.preventDefault();
                    go(e.url);
                  }}
                  data-testid="search-evidence"
                >
                  {t(searchKindLabelOf(e.kind, e.status))}　{e.title}
                  {/* 件名の無い帳票は題が番号＝番号を2回出さない（本番の確かめ「Q-0007（Q-0007）」） */}
                  {e.docNo && e.docNo !== e.title ? `（${e.docNo}）` : ""}
                </a>
              ))}
            </div>
          ) : null}
          {answer.data?.usage ? (
            <p className="tsearch-ansmsg">
              {SEARCH_MSG.aiUsage}
              <button type="button" className="btn text" onClick={(e) => onHelp(e.currentTarget)} data-testid="search-usage-help">
                {SEARCH_MSG.aiUsageLink}
              </button>
            </p>
          ) : null}
        </section>
      ) : null}
      {prices !== null ? (
        <section className="tsearch-prices" data-testid="search-prices">
          <div className="tsearch-grouphead">
            <h3>{SEARCH_MSG.unitPricesTitle.replace("{name}", prices)}</h3>
            <span className="sp" />
            <button type="button" className="btn text" onClick={() => setPrices(null)}>
              {SEARCH_MSG.close}
            </button>
          </div>
          {priceQ.data ? (
            priceQ.data.items.length === 0 ? (
              <p className="tsearch-empty">{SEARCH_MSG.unitPricesEmpty}</p>
            ) : (
              <>
                <p className="tsearch-stats" data-testid="search-prices-stats">
                  {SEARCH_MSG.unitPricesStats
                    .replace("{n}", String(priceQ.data.stats.count))
                    .replace("{min}", yenText(priceQ.data.stats.min ?? 0))
                    .replace("{median}", yenText(priceQ.data.stats.median ?? 0))
                    .replace("{max}", yenText(priceQ.data.stats.max ?? 0))}
                </p>
                <ul className="tsearch-list">
                  {priceQ.data.items.map((x, i) => (
                    <li key={i}>
                      <a
                        href={x.url}
                        className="srow"
                        data-srow
                        onClick={(e) => {
                          e.preventDefault();
                          go(x.url);
                        }}
                      >
                        <span className="srow-title">{x.unitPrice != null ? yenText(x.unitPrice) : "—"}</span>
                        <span className="srow-sub">
                          {[ymdShort(x.date), x.partnerName, x.quantity != null ? `${x.quantity}${x.unit}` : "", x.docNo].filter(Boolean).join("・")}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )
          ) : (
            <p className="tsearch-empty">…</p>
          )}
        </section>
      ) : (
        <div id={listId} className={"tsearch-results" + (result.isFetching ? " is-busy" : "")} data-testid="search-results">
          {debounced && result.data && groups.length === 0 ? <p className="tsearch-empty">{SEARCH_MSG.empty}</p> : null}
          {result.isError ? <p className="tsearch-empty">{SEARCH_MSG.error}</p> : null}
          {groups.map((g) => {
            const all = groupOf(g.kind);
            const hits = all ? all.hits : g.hits;
            return (
              <section key={g.kind} className="tsearch-group" data-testid={`search-group-${g.kind}`}>
                <div className="tsearch-grouphead">
                  {/* 取引先の段は当たった取引先の種類で（{vendor}だけ＝{vendor}・混ざる／両方＝「{customer}・{vendor}」。#233 §13.7 ヘ） */}
                  <h3>{t(searchGroupLabelOf(g.kind, hits.map((h) => h.status)))}</h3>
                  <span className="tsearch-count">{g.total}件</span>
                </div>
                <ul className="tsearch-list">
                  {hits.map((h) => (
                    <HitRow key={`${h.kind}:${h.id}`} h={h} go={go} onPrices={(name) => setPrices(name)} />
                  ))}
                </ul>
                {g.total > g.hits.length && expanded !== g.kind ? (
                  <button type="button" className="btn text tsearch-all" onClick={() => setExpanded(g.kind)} data-testid={`search-all-${g.kind}`}>
                    {SEARCH_MSG.all.replace("{n}", String(g.total))}
                  </button>
                ) : null}
                {expanded === g.kind ? (
                  <button type="button" className="btn text tsearch-all" onClick={() => setExpanded(null)}>
                    {SEARCH_MSG.less}
                  </button>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </>
  );

  return { q, setQ, ask, answer, inputId, listId, body, ai };
}

const PDF_PATH: Partial<Record<SearchKind, (id: string) => string>> = {
  estimate: (id) => `/estimates/${id}/paper`,
  invoice: (id) => `/invoices/${id}/paper`,
  receipt: (id) => `/receipts/${id}/paper`,
};

function HitRow({ h, go, onPrices }: { h: SearchHit; go: (url: string) => void; onPrices: (name: string) => void }) {
  const status = t(searchStatusLabel(h.kind, h.status));
  const sub = [h.docNo, h.context, h.date ? ymdShort(h.date) : ""].filter(Boolean).join("・");
  const pdf = PDF_PATH[h.kind];
  return (
    <li className="srow-li" data-testid="search-hit">
      <a
        href={h.url}
        className="srow"
        data-srow
        onClick={(e) => {
          e.preventDefault();
          go(h.url);
        }}
        onKeyDown={(e) => {
          if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
          e.preventDefault();
          const all = [...(e.currentTarget.closest(".tsearch-results, .tsearch-body, .tsearch-pop")?.querySelectorAll<HTMLElement>("[data-srow]") ?? [])];
          const i = all.indexOf(e.currentTarget);
          all[e.key === "ArrowDown" ? Math.min(all.length - 1, i + 1) : Math.max(0, i - 1)]?.focus();
        }}
      >
        <span className="srow-title">
          {/* 題は自分の箱で「…」（flex の箱に直に置くと「…」が付かず、長い題が状態のチップを押し出して見えなくした＝10/21 本番の確かめ） */}
          <span className="srow-name" title={h.title || h.docNo || undefined}>{h.title || h.docNo || "—"}</span>
          {status ? <span className="chip srow-chip">{status}</span> : null}
          {h.viaContact ? <span className="chip srow-chip">{SEARCH_MSG.contactMatch}</span> : null}
        </span>
        {sub ? <span className="srow-sub">{sub}</span> : null}
      </a>
      {h.amount != null ? <span className="srow-amt">{yenText(h.amount)}</span> : null}
      <span className="srow-acts">
        <button type="button" className="btn text srow-detail" onClick={() => go(h.url)}>
          {SEARCH_MSG.detail}
        </button>
        {pdf ? (
          <button type="button" className="btn text" onClick={() => go(pdf(h.id))}>
            {SEARCH_MSG.pdf}
          </button>
        ) : null}
        {h.kind === "line" ? (
          <button type="button" className="btn text" onClick={() => onPrices(h.title)} data-testid="search-prices-open">
            {SEARCH_MSG.unitPrices}
          </button>
        ) : null}
      </span>
    </li>
  );
}
